import { useAvatar } from "@features/profile/hooks/use-avatar.hook";
import { Avatar, AvatarFallback, AvatarImage } from "@shared/ui/avatar";
import { Button } from "@shared/ui/button";
import { Loader2, Trash2, Upload } from "lucide-react";
import { useRef } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { toast } from "sonner";

type Props = {
	avatarUrl: string | null;
	firstName: string;
	lastName: string;
};

export function AvatarUpload({ avatarUrl, firstName, lastName }: Props) {
	const intl = useIntl();
	const { upload, remove } = useAvatar();
	const inputRef = useRef<HTMLInputElement>(null);

	const initials = `${firstName[0] ?? ""}${lastName[0] ?? ""}`.toUpperCase();

	const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;

		const maxSize = 2 * 1024 * 1024;
		if (file.size > maxSize) {
			toast.error(
				intl.formatMessage({
					id: "page.profile.avatar.fileTooLarge",
					defaultMessage: "File size must be less than 2MB",
				})
			);
			return;
		}

		await upload.mutateAsync(file);
		e.target.value = "";
	};

	return (
		<div className="flex items-center gap-5">
			<Avatar className="h-20 w-20 rounded-xl">
				<AvatarImage src={avatarUrl ?? undefined} alt={initials} />
				<AvatarFallback className="rounded-xl text-lg bg-primary text-primary-foreground">
					{initials}
				</AvatarFallback>
			</Avatar>

			<div className="space-y-2">
				<div className="flex items-center gap-2">
					<Button
						type="button"
						variant="outline"
						size="sm"
						disabled={upload.isPending}
						onClick={() => inputRef.current?.click()}
					>
						{upload.isPending ? (
							<Loader2 className="h-4 w-4 animate-spin" />
						) : (
							<Upload className="h-4 w-4" />
						)}
						<FormattedMessage id="page.profile.avatar.upload" defaultMessage="Upload" />
					</Button>
					{avatarUrl && (
						<Button
							type="button"
							variant="ghost"
							size="sm"
							disabled={remove.isPending}
							onClick={() => remove.mutate()}
							className="text-muted-foreground hover:text-destructive"
						>
							{remove.isPending ? (
								<Loader2 className="h-4 w-4 animate-spin" />
							) : (
								<Trash2 className="h-4 w-4" />
							)}
						</Button>
					)}
				</div>
				<p className="text-xs text-muted-foreground">
					<FormattedMessage
						id="page.profile.avatar.hint"
						defaultMessage="PNG, JPG or WEBP. Max 2MB."
					/>
				</p>
			</div>

			<input
				ref={inputRef}
				type="file"
				accept="image/png,image/jpeg,image/webp"
				className="hidden"
				onChange={handleFileChange}
			/>
		</div>
	);
}
